import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { MapPin, Phone, Mail, Clock, ExternalLink, PlayCircle, Images, X } from 'lucide-react';
import { useContent } from '../hooks/useContent';
import { SectionHeading } from './SectionHeading';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (src) => (!src ? '' : src.startsWith('http') ? src : `${BACKEND_URL}${src}`);

// Convert a YouTube / Vimeo / plain video URL into an embeddable src.
const toEmbedUrl = (url) => {
  if (!url) return null;
  try {
    // YouTube — handle youtu.be, youtube.com/watch, embed already
    const yt1 = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{11})/);
    if (yt1) return { kind: 'iframe', src: `https://www.youtube.com/embed/${yt1[1]}?autoplay=1&rel=0` };
    // Vimeo
    const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (vm) return { kind: 'iframe', src: `https://player.vimeo.com/video/${vm[1]}?autoplay=1` };
    // Direct video file
    if (/\.(mp4|webm|mov)(\?|$)/i.test(url)) return { kind: 'video', src: resolveImg(url) };
    // Fallback — open in a new tab
    return { kind: 'link', src: url };
  } catch {
    return { kind: 'link', src: url };
  }
};

const Branches = () => {
  const { data, loading } = useContent('branches', []);
  const branches = data || [];
  const [videoBranch, setVideoBranch] = useState(null);
  const [galleryBranch, setGalleryBranch] = useState(null);
  const [galleryIndex, setGalleryIndex] = useState(0);

  if (!loading && branches.length === 0) return null;

  const openVideo = (b) => setVideoBranch(b);
  const openGallery = (b) => { setGalleryBranch(b); setGalleryIndex(0); };
  const embed = videoBranch ? toEmbedUrl(videoBranch.video_url) : null;
  const galleryImages = (galleryBranch?.gallery_urls || []).filter(Boolean);

  return (
    <section
      id="branches"
      className="py-20 md:py-24 bg-gradient-to-b from-white to-blue-50/40"
      data-testid="branches-section"
    >
      <div className="max-w-7xl mx-auto px-4">
        <SectionHeading
          eyebrow="Get in Touch"
          title="Our"
          accent="Branches"
          subtitle="Find your nearest EWRF branch across Malaysia."
        />

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {branches.map((b) => (
            <Card
              key={b.id}
              className="border-2 border-gray-100 hover:border-[#1565C0]/30 hover:shadow-2xl transition-all group"
              data-testid="branch-card"
            >
              <CardContent className="p-6">
                <div className="h-1 -mx-6 -mt-6 mb-4 bg-gradient-to-r from-[#0D47A1] via-[#1976D2] to-[#42A5F5] rounded-t-md"></div>
                <h3 className="text-lg md:text-xl font-bold text-[#0A1628] mb-3 group-hover:text-[#1565C0] transition-colors">
                  {b.name}
                </h3>
                <ul className="space-y-2.5 text-sm text-gray-700">
                  {b.address && (
                    <li className="flex items-start gap-2.5">
                      <MapPin className="w-4 h-4 mt-0.5 text-orange-500 flex-shrink-0" />
                      <span>{b.address}</span>
                    </li>
                  )}
                  {b.phone && (
                    <li className="flex items-center gap-2.5">
                      <Phone className="w-4 h-4 text-orange-500 flex-shrink-0" />
                      <a href={`tel:${b.phone}`} className="hover:text-[#1565C0] transition-colors">
                        {b.phone}
                      </a>
                    </li>
                  )}
                  {b.email && (
                    <li className="flex items-center gap-2.5">
                      <Mail className="w-4 h-4 text-orange-500 flex-shrink-0" />
                      <a href={`mailto:${b.email}`} className="hover:text-[#1565C0] transition-colors break-all">
                        {b.email}
                      </a>
                    </li>
                  )}
                  {b.hours && (
                    <li className="flex items-center gap-2.5">
                      <Clock className="w-4 h-4 text-orange-500 flex-shrink-0" />
                      <span>{b.hours}</span>
                    </li>
                  )}
                </ul>
                {(b.map_url || b.name) && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    <a
                      href={b.map_url || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(b.name + ' Malaysia')}`}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#1565C0]/10 hover:bg-[#1565C0] text-[#1565C0] hover:text-white text-[11px] font-bold uppercase tracking-wider transition-colors"
                      data-testid="branch-map-btn"
                    >
                      <MapPin className="w-3 h-3" /> Google Maps
                    </a>
                    <a
                      href={b.waze_url || `https://waze.com/ul?q=${encodeURIComponent(b.name + ' Malaysia')}&navigate=yes`}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#33CCFF]/10 hover:bg-[#33CCFF] text-[#0EA5E9] hover:text-white text-[11px] font-bold uppercase tracking-wider transition-colors"
                      data-testid="branch-waze-btn"
                    >
                      <ExternalLink className="w-3 h-3" /> Waze
                    </a>
                    {b.video_url && (
                      <button
                        type="button"
                        onClick={() => openVideo(b)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-rose-100 hover:bg-rose-600 text-rose-700 hover:text-white text-[11px] font-bold uppercase tracking-wider transition-colors"
                        data-testid="branch-video-btn"
                      >
                        <PlayCircle className="w-3 h-3" /> Video
                      </button>
                    )}
                    {Array.isArray(b.gallery_urls) && b.gallery_urls.length > 0 && (
                      <button
                        type="button"
                        onClick={() => openGallery(b)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-100 hover:bg-amber-600 text-amber-800 hover:text-white text-[11px] font-bold uppercase tracking-wider transition-colors"
                        data-testid="branch-gallery-btn"
                      >
                        <Images className="w-3 h-3" /> Gallery ({b.gallery_urls.length})
                      </button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Video modal */}
      <Dialog open={!!videoBranch} onOpenChange={(v) => !v && setVideoBranch(null)}>
        <DialogContent className="max-w-3xl" data-testid="branch-video-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PlayCircle className="w-5 h-5 text-rose-600" />
              {videoBranch?.name} — Video
            </DialogTitle>
          </DialogHeader>
          {embed?.kind === 'iframe' && (
            <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
              <iframe
                src={embed.src}
                className="absolute inset-0 w-full h-full"
                title={`${videoBranch?.name} video`}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          )}
          {embed?.kind === 'video' && (
            <video src={embed.src} controls autoPlay className="w-full rounded-lg bg-black">
              Sorry, your browser doesn&apos;t support embedded videos.
            </video>
          )}
          {embed?.kind === 'link' && (
            <div className="text-center py-8">
              <p className="text-sm text-gray-600 mb-3">Video hosted externally.</p>
              <a href={embed.src} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-rose-600 text-white text-sm font-bold hover:bg-rose-700">
                <ExternalLink className="w-4 h-4" /> Open Video
              </a>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Gallery modal (thumb grid + lightbox click) */}
      <Dialog open={!!galleryBranch} onOpenChange={(v) => !v && setGalleryBranch(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="branch-gallery-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Images className="w-5 h-5 text-amber-600" />
              {galleryBranch?.name} — Photo Gallery ({galleryImages.length})
            </DialogTitle>
          </DialogHeader>
          {galleryImages.length > 0 && (
            <>
              <div className="rounded-lg overflow-hidden bg-black mb-3 relative">
                <img
                  src={resolveImg(galleryImages[galleryIndex])}
                  alt={`${galleryBranch?.name} ${galleryIndex + 1}`}
                  className="w-full max-h-[60vh] object-contain bg-black"
                />
                <div className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full">
                  {galleryIndex + 1} / {galleryImages.length}
                </div>
              </div>
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                {galleryImages.map((src, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setGalleryIndex(i)}
                    className={`aspect-square rounded-md overflow-hidden border-2 transition-all ${
                      i === galleryIndex ? 'border-amber-500 ring-2 ring-amber-300' : 'border-transparent opacity-70 hover:opacity-100'
                    }`}
                    data-testid={`branch-gallery-thumb-${i}`}
                  >
                    <img src={resolveImg(src)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default Branches;
